/**
 * Shared, frozen pre-invest contract types (spec-07 task R0).
 *
 * Read-only for the parallel builders (tasks A–E): these mirror the backend API
 * shapes served under `/api/v1/preinvest`. Code/identifiers are English; the
 * label maps below are the Dutch domain language shown in the UI.
 */

import type { UserRole } from '@/auth/useMe'
import { formatDatum } from '@/lib/format'

export type DealPhase =
  | 'inflow'
  | 'kennismaking'
  | 'verdieping'
  | 'ic'
  | 'closing'
  | 'portfolio'

export type DealStatus = 'active' | 'rejected' | 'latent'

/* --------------------------------------------------------------------------
 * Rejection reasons (spec-60): one admin-managed list in the backend
 * (`dealflow.rejection_reasons`), read via GET /preinvest/rejection-reasons —
 * see `features/preinvest/lifecycle`. Codes are stable strings; labels live in
 * the table, so the frontend keeps no copy of the list.
 * ------------------------------------------------------------------------ */

/**
 * Derived NDA indicator on a startup (spec-08): `none` = no NDA yet (red),
 * `pending` = request in flight (amber), `signed` = founder signed (green).
 */
export type NdaIndicator = 'none' | 'pending' | 'signed'

/**
 * Rabo Innovation Loan (RIL) status, declared on the website application
 * (`null` = unknown / not on file). `requested` and `received` are the
 * noteworthy signals surfaced on cards; `not_requested` shows in the dossier.
 */
export type RilStatus = 'requested' | 'not_requested' | 'received'

/* --------------------------------------------------------------------------
 * Kennismaking status ladder (spec-17 D4, spec-36 trimmed to 2 rungs).
 * spec-36: follow_up_verstuurd + gesprek_2 removed — everything after the
 * first conversation happens in Verdieping.
 * ------------------------------------------------------------------------ */

/** The two-rung Kennismaking status ladder, in order (AI suggests → human confirms). */
export type KennismakingStatus = 'gesprek_1_gepland' | 'gesprek_1_gehad'

/** Ladder order for rendering the rungs and locating the current/next rung. */
export const KENNISMAKING_STATUS_ORDER: readonly KennismakingStatus[] = [
  'gesprek_1_gepland',
  'gesprek_1_gehad',
] as const

/** Dutch labels for the ladder rungs (hardcoded — no i18n in this app). */
export const KENNISMAKING_STATUS_LABELS: Record<KennismakingStatus, string> = {
  gesprek_1_gepland: '1e gesprek gepland',
  gesprek_1_gehad: '1e gesprek gehad',
}

export function kennismakingStatusLabel(status: KennismakingStatus): string {
  return KENNISMAKING_STATUS_LABELS[status] ?? status
}

/**
 * AI-proposed next Kennismaking rung for a deal (GET
 * /preinvest/deals/:id/kennismaking-suggestion). `available` is `false` (with
 * null fields) when AI is off/unconfigured or the deal is not in the
 * Kennismaking phase, so the dossier can render quietly without error noise.
 */
export interface KennismakingSuggestion {
  current_status: KennismakingStatus | null
  suggested_status: KennismakingStatus | null
  next_action: string | null
  rationale: string | null
  available: boolean
}

/**
 * Where a deal came from (spec-40, widened by spec-60): five categories. `event`,
 * `introductie` and `groepsmail` pair with a named source from the register
 * (`lead_source`); `website` (self-registered via the website form) and `onbekend`
 * carry no name.
 */
export type SourceCategory = 'event' | 'introductie' | 'groepsmail' | 'website' | 'onbekend'

/** The categories that carry a register name (spec-60). */
export type NamedSourceCategory = 'event' | 'introductie' | 'groepsmail'

export function isNamedSource(
  category: SourceCategory | '' | null | undefined,
): category is NamedSourceCategory {
  return category === 'event' || category === 'introductie' || category === 'groepsmail'
}

/** The question above the register combobox, per named category. */
export const SOURCE_NAME_PROMPTS: Record<NamedSourceCategory, string> = {
  event: 'Welk event?',
  introductie: 'Wie introduceerde?',
  groepsmail: 'Welke groepsmail of nieuwsbrief?',
}

/** A named source from the bronnenregister (spec-40). */
export interface LeadSourceRef {
  id: string
  name: string
}

/** A register entry as served by GET /preinvest/lead-sources (spec-40). */
export interface LeadSourceOption {
  id: string
  category: NamedSourceCategory
  name: string
}

export type TaskStatus = 'open' | 'done'

export type TaskPriority = 'low' | 'normal' | 'high'

/**
 * Dossier context for a task (spec-18b): `preinvest` (IC funnel) vs `wasstraat`
 * (post-invest onboarding). The single `dealflow.tasks` table spans both; the
 * global "Mijn taken" view groups on this.
 */
export type DossierContext = 'preinvest' | 'wasstraat'

/**
 * Board lanes: the five funnel phases, in order. `portfolio` is the post-invest
 * hand-off (not a board lane); `rejected` is a status, not a lane.
 */
export const LANES: readonly DealPhase[] = [
  'inflow',
  'kennismaking',
  'verdieping',
  'ic',
  'closing',
] as const

/** spec-60: the `inflow` phase reads "Leads" in the UI (the API value stays). */
export const PHASE_LABELS: Record<DealPhase, string> = {
  inflow: 'Leads',
  kennismaking: 'Kennismaking',
  verdieping: 'Verdieping',
  ic: 'IC',
  closing: 'Closing',
  portfolio: 'Portfolio',
}

/** Per-lane accent colour (left bar / chips) — the schets funnel palette. */
export const LANE_COLORS: Record<DealPhase, string> = {
  inflow: '#fecb40',
  kennismaking: '#f99e1f',
  verdieping: '#f47c20',
  ic: '#e23120',
  closing: '#dc2726',
  portfolio: '#9a6b00',
}

/** spec-60: the `latent` status reads "Geparkeerd" in the UI (the API value stays). */
export const STATUS_LABELS: Record<DealStatus, string> = {
  active: 'Actief',
  rejected: 'Afgewezen',
  latent: 'Geparkeerd',
}

/** Full Dutch label for the dossier RIL row (the `null`/unknown case is handled
 *  by {@link rilStatusDetailLabel}). */
export const RIL_STATUS_DETAIL_LABELS: Record<RilStatus, string> = {
  requested: 'Aangevraagd',
  not_requested: 'Niet aangevraagd',
  received: 'Ontvangen',
}

/** Dutch labels for the source-category select (spec-40, five since spec-60). */
export const SOURCE_CATEGORY_LABELS: Record<SourceCategory, string> = {
  event: 'Event',
  introductie: 'Introductie',
  groepsmail: 'Groepsmail/nieuwsbrief',
  website: 'Website',
  onbekend: 'Onbekend',
}

/** The fixed select order for the source categories (spec-40, spec-60). */
export const SOURCE_CATEGORIES: readonly SourceCategory[] = [
  'event',
  'introductie',
  'groepsmail',
  'website',
  'onbekend',
] as const

/**
 * How a deal's source reads in one line: the register name for the named
 * categories, the category label otherwise, and "Nog niet ingevuld" when there
 * is none — the state the phase gate refuses, so it is never left blank.
 */
export function dealSourceLabel(deal: {
  source_category: SourceCategory | null
  lead_source: LeadSourceRef | null
}): string {
  if (!deal.source_category) return 'Nog niet ingevuld'
  const named = isNamedSource(deal.source_category)
  if (named && deal.lead_source) return deal.lead_source.name
  return SOURCE_CATEGORY_LABELS[deal.source_category]
}

/** Dutch toast message for the 409 `lead_source_required` gate (spec-40, spec-60). */
export const LEAD_SOURCE_REQUIRED_MESSAGE =
  'Kies eerst een bron voordat je deze deal verplaatst, parkeert of afwijst.'

/**
 * Thrown on the spec-40 mandatory-source 409 (`lead_source_required`): the deal
 * has no `source_category` yet, so phase changes and rejections are blocked
 * until the team picks a bron. Carries the backend's Dutch message for toasts.
 * Lives here (not in an api module) so dialogs can import it without pulling
 * in `@/lib/supabase`, which needs env that tests don't set.
 */
export class LeadSourceRequiredError extends Error {
  constructor(message = LEAD_SOURCE_REQUIRED_MESSAGE) {
    super(message)
    this.name = 'LeadSourceRequiredError'
  }
}

/** Thrown on a 409 `stale_state` (spec-60): someone else changed the deal first. */
export class StaleStateError extends Error {
  constructor(
    message = 'Deze deal is intussen gewijzigd. Ververs het scherm en probeer opnieuw.',
  ) {
    super(message)
    this.name = 'StaleStateError'
  }
}

/**
 * Any other refused lifecycle call (spec-60). `message` is always Dutch text a
 * user may see; `code` is stable to branch on: the backend's own code
 * (`invalid_status`, `invalid_phase`, …), one the client gives a refusal that has
 * none (`invalid_reason_code`, `deal_not_found`, …), or `error` when unknown.
 * Lives here for the same reason as `LeadSourceRequiredError`.
 */
export class TransitionError extends Error {
  readonly code: string
  constructor(message: string, code: string) {
    super(message)
    this.name = 'TransitionError'
    this.code = code
  }
}

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  open: 'Open',
  done: 'Afgerond',
}

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Laag',
  normal: 'Normaal',
  high: 'Hoog',
}

/** Dutch labels for the dossier context, shown as group headings in "Mijn taken". */
export const DOSSIER_CONTEXT_LABELS: Record<DossierContext, string> = {
  preinvest: 'Pre-invest',
  wasstraat: 'Wasstraat',
}

/** A task row (GET /preinvest/tasks). */
export interface Task {
  id: string
  title: string
  status: TaskStatus
  priority: TaskPriority
  due_date: string | null
  /** Denormalized primary owner (spec-21 D2): always `assignee_ids[0]` or null. */
  assignee_id: string | null
  /** The full assignee set, primary owner first (spec-21 D2). */
  assignee_ids: string[]
  deal_id: string | null
  startup_id: string | null
  source: string
  /** Dossier context (spec-18b): preinvest (IC funnel) vs wasstraat (onboarding). */
  context: DossierContext
  /** The wasstraat onboarding item this task hangs off, when context is wasstraat. */
  wasstraat_item_id: string | null
  created_at: string
  archived_at: string | null
  /** Resolved creator name for display (spec-21 WP8). */
  created_by_name: string | null
}

/* --------------------------------------------------------------------------
 * Unified task overview (spec-21 D6) — platform + founder-portal tasks merged.
 * ------------------------------------------------------------------------ */

/** Degrade status of the portal read: the overview never fails on portal issues. */
export type PortalStatus = 'ok' | 'unavailable' | 'not_configured'

/**
 * One assignee on a unified-overview task (spec-21 D5). Platform assignees
 * always resolve; portal assignees match `core.users` on lowercased email —
 * `platform_user_id` is null for portal-only people.
 */
export interface UnifiedAssignee {
  platform_user_id: string | null
  display_name: string
  email: string | null
}

/**
 * A row from GET /preinvest/tasks/overview (spec-21 D6): a platform or portal
 * task, normalized. `status`/`priority` are plain strings: portal tasks carry
 * the same value sets but aren't bound to the platform enums.
 */
export interface UnifiedTask {
  id: string
  title: string
  status: string
  priority: string
  due_date: string | null
  deal_id: string | null
  startup_id: string | null
  wasstraat_item_id: string | null
  created_at: string
  source_system: 'platform' | 'portal'
  context: DossierContext | 'portal' | 'relaties'
  /** spec-23: unified startup display name (platform: core.startups.legal_name;
   * portal: the portal's own startup name). Null for standalone tasks. */
  startup_name: string | null
  /** spec-23: resolved creator name; platform rows only (portal rows are null). */
  created_by_name: string | null
  /** spec-24: portal internal-note tasks may link to a pillar
   * (Commercial/Technology/Organisation/Finance). Platform rows: null. */
  pillar: string | null
  /** spec-24: portal biweekly link (portal column `biweekly_reference_date`).
   * Platform rows: null. */
  biweekly_date: string | null
  /** spec-24b: owning fund manager display name; portal rows only
   * (platform rows are null — they use `assignees`). */
  assignee_name: string | null
  assignees: UnifiedAssignee[]
  /** spec-63 E4 (ED-53): the relation chip and ↻. Platform relation tasks only. */
  person_id?: string | null
  person_name?: string | null
  organisation_id?: string | null
  organisation_name?: string | null
  agreement_id?: string | null
  agreement_title?: string | null
  agreement_schedule?: 'geen' | 'eenmalig' | 'terugkerend' | 'contactritme' | null
  agreement_rhythm?: string | null
}

/** GET /preinvest/tasks/overview response. */
export interface TasksOverview {
  tasks: UnifiedTask[]
  portal_status: PortalStatus
}

/** A team-member option for assignee pickers (GET /users). */
export interface UserOption {
  id: string
  display_name: string
  role: UserRole
}

/**
 * The person who last touched a deal (status change / note / task / document),
 * derived backend-side. Drives the board card's "last worker" avatar. `null`
 * when there is no recorded activity and no owner/creator.
 */
export interface LastActor {
  user_id: string
  full_name: string
  initials: string
}

/** A card on the board / a row in deal lists (GET /preinvest/deals). */
export interface DealListItem {
  id: string
  phase: DealPhase
  status: DealStatus
  owner_id: string | null
  startup: {
    id: string
    legal_name: string
    sector: string | null
    source: string | null
    city?: string | null
    /** Signed URL for the startup logo, or null when no logo is set. */
    logo_url: string | null
    /** True when the startup also applied at Percival Participations. */
    percival_match: boolean
  }
  days_in_phase: number
  open_task_count: number
  next_task: {
    id: string
    title: string
    due_date: string | null
  } | null
  last_actor: LastActor | null
  /** Derived per-startup NDA indicator (spec-08): none/pending/signed. */
  nda_indicator: NdaIndicator
  /** Rabo Innovation Loan status from the website application, or null (unknown). */
  ril_status: RilStatus | null
  /** The latest pitch deck on the deal, or null when none is uploaded. */
  pitch_deck: { id: string; filename: string } | null
  /** Structured lead source (spec-17 D2 / spec-40), or null when unconfirmed. */
  source_category: SourceCategory | null
  /** Named bronnenregister source (spec-40); set for event/introductie deals. */
  lead_source: LeadSourceRef | null
  /** Kennismaking status (spec-17 D4), or null until the first step is set. */
  kennismaking_status: KennismakingStatus | null
  /**
   * Latest completed gap_analysis VONK-fit match-score (spec-17): the numeric
   * score and a human label. Both null when the deal has no completed gap
   * analysis.
   */
  gap_score: number | null
  gap_label: string | null
  /**
   * Aggregated questionnaire status on the deal (additive backend field):
   * `open` = form instances the founder still has to finish (sent +
   * in_progress), `submitted` = completed ones. Drafts never count. Optional
   * so older payloads (without the field) keep parsing.
   */
  forms?: { open: number; submitted: number } | null
  /**
   * Optional AI-suggested next action (spec-17, B-Kennismaking-AI). Rendered as
   * the spine's "next action" fallback when there is no `next_task`. Optional /
   * render-when-present so the card carries no cross-builder dependency.
   */
  next_action?: { label: string } | null
  /** Up to 10 open tasks for inline display on board cards (spec-21 WP8). */
  open_tasks: Task[]
  created_at: string
  /** spec-22: return date for latent deals, null otherwise. */
  latent_until: string | null
}

/** A light card in the board's Leads lane (GET /preinvest/leads/lane, spec-60). */
export interface LeadCardItem {
  id: string
  startup: {
    id: string
    legal_name: string
    website: string | null
    sector: string | null
    one_liner: string | null
    percival_match: boolean
  }
  source_category: SourceCategory | null
  lead_source: LeadSourceRef | null
  /** "Lead sinds": the date of first contact. */
  lead_at: string
  /** When it (re)entered Leads: created, reopened or restored. Drives "new". */
  in_leads_since: string
  days_in_leads: number
  /** "In Leads sinds" within the 14-day window: the card's "Nieuw" marker. */
  is_new: boolean
  owner: LastActor | null
  nda_indicator: NdaIndicator
  has_pitch_deck: boolean
  previously_rejected_at: string | null
  /** The website form's "Hoe heeft u van ons gehoord?" answer. */
  heard_about_us: string | null
}

/**
 * GET /preinvest/leads/lane: all active leads (newest "In Leads sinds" first, capped
 * at `limit`) plus the counters. `total_new` / `older_count` split `total_all` by the
 * 14-day window.
 */
export interface LeadLane {
  items: LeadCardItem[]
  total_new: number
  total_all: number
  older_count: number
  window_days: number
  limit: number
}

/** One time we came across the startup — a "bronmoment" (spec-60). */
export interface LeadTouch {
  id: string
  occurred_on: string
  /** website | handmatig | backfill | import (spec-60 B2: an Excel import). */
  origin: string
  source_category: SourceCategory | null
  lead_source: LeadSourceRef | null
  note: string | null
}

/** A possible duplicate from GET /preinvest/lead-duplicates (spec-60). */
export interface DuplicateMatch {
  startup_id: string
  legal_name: string
  match: 'kvk' | 'website' | 'naam' | 'e_maildomein' | 'lijkt_op'
  strength: 'zeker' | 'sterk' | 'mogelijk'
  /** The startup's deal; null for a startup without one. */
  deal_id: string | null
  phase: DealPhase | null
  status: DealStatus | null
  latent_until: string | null
  /** For a rejected deal: when, and the label of the reason. */
  rejected_at: string | null
  rejection_label: string | null
}

/** A contact/founder on the deal dossier. */
export interface Contact {
  person_id: string
  full_name: string
  email: string | null
  salutation: string | null
  title: string | null
  phone: string | null
  role: string
}

/**
 * One row of the deal's phase/status history timeline.
 *
 * spec-60: rejection rows carry the effective code from the managed list, its
 * label and the note — the backend reads old "Categorie — notitie" rows the same
 * way. The spec-60 fields are optional here (the backend always sends them) so
 * older fixtures keep compiling; a missing `note` means a pre-spec-60 payload.
 */
export interface TimelineEntry {
  /** History row id (spec-60; undo). */
  id?: string
  occurred_at: string
  from_phase: DealPhase | null
  to_phase: DealPhase | null
  from_status: DealStatus | null
  to_status: DealStatus | null
  reason: string | null
  /** spec-60, rejection rows: the effective code, its label and the note. */
  reason_code?: string | null
  reason_label?: string | null
  note?: string | null
  /** spec-60: the return date a park row set (null = parked without a date). */
  latent_until?: string | null
  actor_id: string | null
}

/**
 * A single `deal_status_history` row served by the reject/reason-edit
 * endpoints (spec-36 PR I). Kept separate from `TimelineEntry` so the
 * rejection dialogs can consume it without pulling the full timeline shape.
 */
export interface DealStatusHistoryEntry {
  id: string
  occurred_at: string
  from_phase: DealPhase | null
  to_phase: DealPhase | null
  from_status: DealStatus | null
  to_status: DealStatus | null
  /** Free-text explanation (was the only field before spec-36). */
  reason: string | null
  /** spec-60: a code from the managed rejection-reason list; null on other rows. */
  reason_code: string | null
  actor_id: string | null
}

/** Full deal dossier (GET /preinvest/deals/:id). */
export interface DealDetail {
  deal: {
    id: string
    phase: DealPhase
    status: DealStatus
    owner_id: string | null
    investment_amount_eur: string
    equity_percentage: string
    /** Rabo Innovation Loan status from the website application, or null (unknown). */
    ril_status: RilStatus | null
    /** Structured lead source (spec-17 D2 / spec-40), or null when unconfirmed. */
    source_category: SourceCategory | null
    /** Named bronnenregister source (spec-40); set for event/introductie deals. */
    lead_source: LeadSourceRef | null
    /** Kennismaking status (spec-17 D4), or null until the first step is set. */
    kennismaking_status: KennismakingStatus | null
    /** True when a portfolio deal has no submitted IC card at all; false otherwise. */
    ic_scores_missing: boolean
    created_at: string
    /** spec-60 "Lead sinds" (`yyyy-mm-dd`); optional so older fixtures compile. */
    lead_at?: string
    /** spec-60 B2 "Voorstel ontvangen op" (`yyyy-mm-dd`); null = signalled only. */
    proposal_received_on?: string | null
    /** spec-60: the return date of a parked deal (null = not parked, or no date). */
    latent_until?: string | null
    /**
     * spec-60: whole active days in the current phase, on the board's pause clock
     * (parked time does not count). Optional for the same reason as `lead_at`.
     */
    days_in_phase?: number
  }
  startup: {
    id: string
    legal_name: string
    kvk_number: string | null
    sector: string | null
    source: string | null
    website: string | null
    one_liner: string | null
    /** Company address (spec-19b); null until captured by an inflow form/edit. */
    street: string | null
    house_number: string | null
    postcode: string | null
    city: string | null
    /** Signed URL for the startup logo, or null when no logo is set. */
    logo_url: string | null
    /** True when the startup also applied at Percival Participations. */
    percival_match: boolean
  }
  contacts: Contact[]
  tasks: Task[]
  timeline: TimelineEntry[]
  last_actor: LastActor | null
  /** Derived per-startup NDA indicator (spec-08): none/pending/signed. */
  nda_indicator: NdaIndicator
  placeholders: string[]
  /**
   * spec-60: every time we came across the startup, newest first. Optional (like
   * the other spec-60 fields) so older fixtures compile; the backend always sends it.
   */
  touches?: LeadTouch[]
  /** spec-60 "Gehoord via": the website form's "Hoe heeft u van ons gehoord?". */
  heard_about_us?: string | null
  /** spec-64 U-116: the newest note or mail of the deal; null = no contact recorded yet. */
  last_contact?: LastContact | null
  /** spec-64 U-099: the post-invest dossier of this deal (Portfolio only), or null. */
  portfolio_item_id?: string | null
}

/** The newest recorded contact on a deal (a note or a mail). */
export interface LastContact {
  at: string
  kind: 'note' | 'mail'
  /** Author of the note, sender of the mail; null when unknown. */
  by: string | null
}

/** A row in the startup-dossier list (GET /preinvest/dossiers). */
export interface DossierListItem {
  deal_id: string
  startup: {
    id: string
    legal_name: string
    sector: string | null
    city: string | null
    one_liner: string | null
    /** Signed URL for the startup logo, or null when no logo is set. */
    logo_url: string | null
    /** True when the startup also applied at Percival Participations. */
    percival_match: boolean
  }
  phase: DealPhase
  status: DealStatus
  open_task_count: number
  /** Founder/contact names for avatars + labels, sorted alphabetically. */
  founders: string[]
  /** The deal owner (avatar + name), or null when unassigned. */
  owner: LastActor | null
  /** Whoever last touched the deal, or null with no recorded activity. */
  last_actor: LastActor | null
  /** Derived per-startup NDA indicator (spec-08): none/pending/signed. */
  nda_indicator: NdaIndicator
  /** Rabo Innovation Loan status from the website application, or null (unknown). */
  ril_status: RilStatus | null
  /** The latest pitch deck on the deal, or null when none is uploaded. */
  pitch_deck: { id: string; filename: string } | null
  days_in_phase: number
  /** spec-22: return date for latent deals, null otherwise. */
  latent_until: string | null
  /**
   * Composed "Categorie — toelichting" rejection reason from the latest
   * `to_status='rejected'` history row; only present on the rejected tab
   * (null/absent everywhere else, and null when no reason was stored).
   */
  rejection_reason?: string | null
  /** ISO timestamp the deal was rejected at; only present on the rejected tab. */
  rejected_at?: string | null
  /** True when a portfolio deal has no submitted IC card at all; false otherwise. */
  ic_scores_missing?: boolean
}

/**
 * Optional founder contact attached to the minimal "Startup toevoegen" create
 * (spec-08 S9). Only `full_name` is required when a founder is supplied.
 */
export interface CreateDealFounderBody {
  full_name: string
  email?: string | null
  salutation?: string | null
  title?: string | null
}

/**
 * Body for the "Startup toevoegen" create (spec-08, POST
 * /api/v1/preinvest/deals): creates the startup and its inflow deal in one step.
 * Mirrors the public website (NDA) form fields (spec-19b). `legal_name` and
 * (spec-60) `source_category` are required; `kvk_number`/`postcode` are
 * format-checked by the backend only when filled. Plus an optional founder contact.
 */
export interface CreateDealBody {
  legal_name: string
  kvk_number?: string | null
  one_liner?: string | null
  /** Sector from the fixed dropdown (spec-20 C); stored free-text backend-side. */
  sector?: string | null
  street?: string | null
  house_number?: string | null
  postcode?: string | null
  city?: string | null
  founder?: CreateDealFounderBody | null
  source_category: SourceCategory
  /** Named source (spec-40/60): required for event/introductie/groepsmail, forbidden otherwise. */
  source_name?: string | null
  /** spec-60 "Lead sinds" (`yyyy-mm-dd`, not in the future); the backend defaults to today. */
  lead_at?: string
  /** spec-60: stored as an https address; 400 without a real host. */
  website?: string | null
}

/** Result of the minimal create (POST /api/v1/preinvest/deals). */
export interface CreateDealResult {
  deal_id: string
  startup_id: string
  /**
   * spec-60: true when the startup already existed — a KvK match on create (the
   * backend records a bronmoment) or a duplicate opened from the dialog.
   */
  existing?: boolean
}

/** A founder extracted from a pitch deck (spec-17 D9). */
export interface DeckPrefillFounder {
  name: string
  role?: string | null
}

/**
 * Best-effort intake fields extracted from a pitch deck (spec-17 D9, POST
 * /api/v1/preinvest/deals/prefill-from-deck). Every field is best-effort: an
 * empty string / empty list when the deck does not state it.
 */
export interface DeckPrefill {
  legal_name: string
  one_liner: string
  city: string
  website: string
  sector: string
  founders: DeckPrefillFounder[]
}

export function phaseLabel(phase: DealPhase): string {
  return PHASE_LABELS[phase] ?? phase
}

export function statusLabel(status: DealStatus): string {
  return STATUS_LABELS[status] ?? status
}

export function taskStatusLabel(status: TaskStatus): string {
  return TASK_STATUS_LABELS[status] ?? status
}

/** Dutch RIL label for the dossier row; `null` (unknown) → "Onbekend". */
export function rilStatusDetailLabel(status: RilStatus | null): string {
  if (!status) return 'Onbekend'
  return RIL_STATUS_DETAIL_LABELS[status] ?? status
}

export function taskPriorityLabel(priority: TaskPriority): string {
  return TASK_PRIORITY_LABELS[priority] ?? priority
}

export function dossierContextLabel(context: DossierContext): string {
  return DOSSIER_CONTEXT_LABELS[context] ?? context
}

export function formatDate(value: string | null): string {
  return formatDatum(value)
}
